import { SEAT_CARD_WIDTH, SEAT_EDGE_BUFFER_PX } from 'constants/gameConstants';
import { faceDownLeftReach } from 'components/Seat/SeatWithPlayer/holeCardFan';

/**
 * EN-10520 — keep every seat's PAINTED footprint inside the viewport.
 *
 * A seat's `left` is a percentage of the table container, and the ellipse puts
 * the outermost seats at ~10%/90%. On a 390px phone that is only ~39px from the
 * edge — less than the seat's own half-width, and far less once the hole cards
 * are counted. The cards now always hang off the seat's LEFT side (so they read
 * as held in the avatar's right hand and can never be pushed off the RIGHT edge
 * by a right-side seat), which makes the footprint deliberately asymmetric:
 * wide on the left, half a card on the right.
 *
 * These helpers turn that footprint into a CSS `clamp()`, so the ellipse is
 * still the source of truth for seat placement and the clamp only bites at the
 * narrowest widths.
 */

/**
 * How far the hole-card group reaches LEFT of the seat's centre, in unscaled px.
 *
 * Delegated to the same function the renderer uses rather than restated here:
 * these two disagreeing is what silently breaks the viewport clamp, and it has
 * happened. A 4-card Omaha hand reaches further than a 2-card one, so the wider
 * case is the one that has to fit.
 */
export const seatCardLeftOverhang = (isPortrait: boolean, cardCount = 4): number =>
  faceDownLeftReach(cardCount, isPortrait);

/** Painted extent of a seat either side of its `left` anchor, in unscaled px. */
export const seatEdgeInsets = (isPortrait: boolean) => ({
  left: seatCardLeftOverhang(isPortrait),
  // The seat CARD is the widest thing on this side (it is wider than the avatar
  // — see SEAT_CARD_WIDTH), so it is what has to clear the viewport edge.
  right: SEAT_CARD_WIDTH / 2
});

/**
 * The seat wrapper's `left`, clamped so neither the card fan nor the seat card
 * can touch a viewport edge.
 *
 * Both bounds are multiplied by `--seat-scale` because the wrapper scales about
 * its own centre, so the overhang shrinks with the seat on small/grid layouts.
 */
export const seatEdgeSafeLeft = (leftPct: number, isPortrait: boolean): string => {
  const { left, right } = seatEdgeInsets(isPortrait);
  const min = `calc(${left}px * var(--seat-scale, 1) + ${SEAT_EDGE_BUFFER_PX}px)`;
  const max = `calc(100% - ${right}px * var(--seat-scale, 1) - ${SEAT_EDGE_BUFFER_PX}px)`;
  return `clamp(${min}, ${leftPct}%, ${max})`;
};

/**
 * The same clamp as `seatEdgeSafeLeft`, resolved to PIXELS.
 *
 * The CSS above is what the browser paints; this is what anything that has to
 * REASON about where a seat ends up must use. The two live next to each other
 * and share `seatEdgeInsets` because the failure that separates them has
 * already happened: EN-10740's board-vs-seat clearance was modelled against the
 * RAW ring position (`leftPct` as a percentage of the width), and on a 390px
 * phone this clamp moves the left flank seat INBOARD of that — 58.4px instead
 * of 46.8px at the Largest Table View Size, i.e. 11.6px CLOSER to the centred
 * community board than the model believed. So the model certified a clearance
 * the player never got, and the first flop card was painted behind the left
 * flank seat's name plate.
 *
 * Keep the two in step. CSS `clamp(MIN, VAL, MAX)` is defined as
 * `max(MIN, min(VAL, MAX))` — NOT `min(max(VAL, MIN), MAX)`, which differs
 * exactly when MIN exceeds MAX. That is not a hypothetical: a container
 * narrower than both buffers hits it (see the degenerate case in
 * `__tests__/seatEdgeInsets.test.ts`), and the two forms disagree about which
 * edge the seat hugs there.
 */
export const seatSafeLeftPx = (
  leftPct: number,
  isPortrait: boolean,
  containerWidth: number,
  seatScale: number
): number => {
  const { left, right } = seatEdgeInsets(isPortrait);
  const min = left * seatScale + SEAT_EDGE_BUFFER_PX;
  const max = containerWidth - right * seatScale - SEAT_EDGE_BUFFER_PX;
  const preferred = (leftPct / 100) * containerWidth;
  return Math.max(min, Math.min(preferred, max));
};
